// Die Regeln der Handelsideen-Tafel — ohne Netz, damit Seite und Vorlage
// dieselben sind.
//
// Aus einer Idee wird eine ZEILE: Instrument, Vorschlag mit Einstufung und
// Status, rechts der ABSTAND zum Einstieg. Aus den Zeilen werden GRUPPEN —
// nach Abstand, Status oder Instrument. Und aus den Schwellen wird die
// KURSLEITER: wo die Idee widerlegt ist, wo gekauft wird, wo nicht mehr.

import type { Money, TradeIdea } from '../api/client'
import { betrag, kapitalisierung, tag } from '../pages/priceFormat'
import { CLASSIFICATION, RISK_PROFILE, STATUS } from '../pages/tradeIdeaVocabulary'
import type { Ton } from './faelleRegeln'

export type Sicht = 'abstand' | 'status' | 'instrument' | 'branche'

/** Die Farbe des Status — `dark` ist „im Markt": weiß auf dunkel. */
export type StatusTon = 'accent' | 'good' | 'dark' | 'bad' | 'warn' | 'muted'

/** Eine Zeile der Tafel. */
export interface IdeenZeile {
  key: string
  id: number
  idee: TradeIdea
  name: string
  /**
   * „88 Mrd. USD · Halbleiter" — Kapitalisierung und Branche. Geben die
   * Stammdaten beides nicht her, stehen ISIN, Handelsplatz und Währung da;
   * die stehen ohnehin im Popover am Namen.
   */
  ort: string
  headline: string
  klasse: string
  status: string
  statusTon: StatusTon
  /** „ausgewogen · seit 28.08.2026, 16:05" */
  meta: string
  /** „in Zone", „+3,3 %", „—" */
  abstand: string
  /** „kaufbar", „über Zone, noch kaufbar", „ohne Zone" */
  abstandSub: string
  abstandTon: Ton
  /** Fürs Sortieren nach Abstand: 0 in Zone … 9 widerlegt. */
  beendet: boolean
}

export interface IdeenGruppe {
  key: string
  label: string
  sub: string
  zeilen: IdeenZeile[]
}

// ---------------------------------------------------------------- Zahlen

export function zahl(wert: Money | null | undefined): number | null {
  if (!wert) return null
  const n = Number(wert.amount)
  return Number.isFinite(n) ? n : null
}

const PROZENT = new Intl.NumberFormat('de-DE', {
  minimumFractionDigits: 1, maximumFractionDigits: 1,
})

/** (a − b) / b in Prozent. */
export function pct(a: number, b: number): number {
  return ((a - b) / b) * 100
}

/** „+3,3 %", „−1,8 %", „0,0 %" — ein echtes Minus, kein Bindestrich. */
export function pctText(p: number): string {
  const zeichen = p > 0 ? '+' : p < 0 ? '−' : ''
  return `${zeichen}${PROZENT.format(Math.abs(p))} %`
}

export function beendet(status: string): boolean {
  return status === 'INVALIDATED' || status === 'EXPIRED' || status === 'CLOSED'
    || status === 'REJECTED'
}

const STATUS_TON: Record<string, StatusTon> = {
  PROPOSED: 'accent', ACTIVE: 'good', ENTERED: 'dark',
  INVALIDATED: 'bad', EXPIRED: 'warn', CLOSED: 'muted', REJECTED: 'muted',
}

const KURZ = new Intl.DateTimeFormat('de-DE', {
  day: '2-digit', month: '2-digit', year: '2-digit',
})

const UHR = new Intl.DateTimeFormat('de-DE', { hour: '2-digit', minute: '2-digit' })
const TAG_LANG = new Intl.DateTimeFormat('de-DE', {
  weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
})

const ZEIT_LANG = new Intl.DateTimeFormat('de-DE', { dateStyle: 'medium', timeStyle: 'short' })

/** „28.08.2026, 16:05" — Anlage und „seit" in der Zeile, mit Uhrzeit. */
export function zeitLang(iso: string): string { return ZEIT_LANG.format(new Date(iso)) }

/** Ob ein Zeitstempel eine Uhrzeit trägt — „2026-09-04" hat keine. */
function mitUhr(iso: string): boolean { return iso.length > 10 }

/** 04.09.26 — oder „04.09.26, 08:12", wenn der Stempel eine Uhrzeit hat. */
export function tagKurz(iso: string): string {
  const d = new Date(iso)
  return mitUhr(iso) ? `${KURZ.format(d)}, ${UHR.format(d)}` : KURZ.format(d)
}

/** „Freitag, 4. September 2026, 08:12 Uhr" — beim Zeigen auf das Kurzdatum. */
export function tagVoll(iso: string): string {
  const d = new Date(iso)
  return mitUhr(iso) ? `${TAG_LANG.format(d)}, ${UHR.format(d)} Uhr` : TAG_LANG.format(d)
}

// --------------------------------------------------------------- Abstand

/**
 * Wo der Kurs zur Idee steht — die eine Zahl rechts in der Zeile.
 *
 * In Zone heißt kaufbar. Darunter heißt warten (bei „erst nach Bodenbildung"
 * auf den Reclaim), darüber heißt noch kaufbar bis zur Grenze, dahinter
 * nicht mehr nachlaufen. Im Markt zählt der Abstand zum eigenen Einstand,
 * bei „erst bei Auslöser" der Weg bis dorthin. Der Rang ordnet die Liste:
 * was jetzt kaufbar ist, zuerst.
 */
export function abstandAus(e: TradeIdea): {
  abstand: string; abstandSub: string; abstandTon: Ton
} {
  const kurs = zahl(e.last_price)
  const widerlegt = zahl(e.invalid_below)
  const zoneAb = zahl(e.entry_low)
  const zoneBis = zahl(e.entry_high)
  const chase = zahl(e.chase_limit)
  const schnitt = zahl(e.position.average_price) ?? zahl(e.planned_average)

  if (kurs === null) return { abstand: '—', abstandSub: 'kein Kurs', abstandTon: 'dim' }
  if (e.status === 'INVALIDATED') {
    return widerlegt !== null
      ? { abstand: pctText(pct(kurs, widerlegt)), abstandSub: 'unter widerlegt', abstandTon: 'bad' }
      : { abstand: '—', abstandSub: 'ungültig', abstandTon: 'dim' }
  }
  if (beendet(e.status)) {
    const sub = e.status === 'EXPIRED' && e.valid_until
      ? `Frist ${tag.format(new Date(e.valid_until))}`
      : e.status === 'REJECTED' ? 'abgelehnt' : 'beendet'
    return { abstand: '—', abstandSub: sub, abstandTon: 'dim' }
  }
  if (zoneAb === null) return { abstand: '—', abstandSub: 'ohne Zone', abstandTon: 'dim' }
  if (e.status === 'ENTERED') {
    if (schnitt === null) return { abstand: '—', abstandSub: 'im Markt', abstandTon: 'dim' }
    const p = pct(kurs, schnitt)
    return { abstand: pctText(p), abstandSub: `auf Ø ${betrag(String(schnitt))}`,
             abstandTon: p >= 0 ? 'good' : 'bad' }
  }
  if (e.classification === 'TRIGGER_ONLY') {
    const p = pct(kurs, zoneAb)
    return { abstand: pctText(p), abstandSub: 'bis Auslöser', abstandTon: 'muted' }
  }
  const zb = zoneBis ?? zoneAb
  if (kurs >= zoneAb && kurs <= zb) {
    return { abstand: 'in Zone', abstandSub: 'kaufbar', abstandTon: 'good' }
  }
  if (kurs < zoneAb) {
    const p = pct(kurs, zoneAb)
    return { abstand: pctText(p),
             abstandSub: e.classification === 'BOTTOMING_REQUIRED' ? 'unter Zone, Reclaim offen' : 'unter Zone',
             abstandTon: 'warn' }
  }
  const p = pct(kurs, zb)
  const ueberGrenze = chase !== null && kurs > chase
  return { abstand: pctText(p),
           abstandSub: ueberGrenze ? 'über Grenze' : 'über Zone, noch kaufbar',
           abstandTon: ueberGrenze ? 'muted' : 'warn' }
}

// ---------------------------------------------------------------- Zeilen

/** Der umgerechnete Betrag, wenn die Zielwährung dabei ist — sonst nichts. */
export function umgerechnet(wert: Money | null | undefined, ziel: string | undefined): string | null {
  if (!wert || !ziel || ziel === wert.currency) return null
  const um = wert.currencies.find((c) => c.currency === ziel)
  return um ? `≈ ${betrag(um.amount)} ${ziel}` : null
}

// ------------------------------------------------------- Chance / Risiko

/**
 * Chance und Risiko einer Idee, gerechnet aus den Marken — wie in der
 * Vorlage: vom geplanten Schnitt (sonst der Zonenunterkante) bis zum
 * ersten Ziel ist die Chance, bis zur Widerlegt-Marke das Risiko. Beides
 * als Betrag in der Währung der Idee, mit Umrechnung, und als Prozent.
 * Der Score ist das hinterlegte Verhältnis des Analysten, nicht der
 * Quotient — die zwei sollen sich vergleichen lassen.
 */
export interface ChanceRisiko {
  /** „+11,8 %" — oder „—", wenn Ziel oder Marke fehlen. */
  chanceKurz: string
  /** „−5,7 %" */
  risikoKurz: string
  /** Der Betrag bis zum Ziel, positiv, mit Umrechnung. Leer ohne Ziel. */
  chance: Money | null
  /** Der Betrag bis zur Marke, positiv, mit Umrechnung. Leer ohne Marke. */
  risiko: Money | null
  /** „+11,8 % bis Ziel 142,00" — oder, warum es nichts zu rechnen gab. */
  chanceSub: string
  /** „−5,7 % bis Marke 124,40" */
  risikoSub: string
  /** Das hinterlegte Verhältnis, „2,00" — oder „—". */
  score: string
}

/**
 * Die Differenz zweier Beträge derselben Währung, samt Umrechnungen.
 *
 * Nur Zielwährungen, die BEIDE tragen: Fehlt sie bei einem, wäre die
 * Differenz halb umgerechnet und damit falsch.
 */
function differenz(a: Money, b: Money): Money {
  const betragVon = (x: number) => x.toFixed(4)
  return {
    amount: betragVon(Number(a.amount) - Number(b.amount)),
    currency: a.currency,
    currencies: a.currencies.flatMap((ca) => {
      const cb = b.currencies.find((x) => x.currency === ca.currency)
      return cb ? [{ ...ca, amount: betragVon(Number(ca.amount) - Number(cb.amount)) }] : []
    }),
  }
}

export function chanceRisikoAus(e: TradeIdea, ziele: Money[] = []): ChanceRisiko {
  const score = e.risk_reward ? betrag(e.risk_reward) : '—'
  const basisGeld = e.planned_average ?? e.entry_low
  // Das erste Ziel kommt mit der Liste; das Detail bringt alle.
  const zielGeld = ziele[0] ?? e.first_target ?? null
  const markeGeld = e.invalid_below
  const basis = zahl(basisGeld), ziel = zahl(zielGeld), marke = zahl(markeGeld)
  const leer = {
    chanceKurz: '—', risikoKurz: '—', chance: null, risiko: null, score,
    chanceSub: basis === null ? 'kein Schnitt und keine Zone'
      : ziel === null ? 'kein Ziel hinterlegt' : '',
    risikoSub: marke === null ? 'keine Widerlegt-Marke' : '',
  }
  if (basis === null || basis === 0 || !basisGeld) return leer
  const chance = ziel !== null && zielGeld ? {
    kurz: pctText(pct(ziel, basis)),
    betrag: differenz(zielGeld, basisGeld),
    sub: `${pctText(pct(ziel, basis))} bis Ziel ${betrag(zielGeld.amount)}`,
  } : null
  const risiko = marke !== null && markeGeld ? {
    kurz: pctText(pct(marke, basis)),
    betrag: differenz(basisGeld, markeGeld),
    sub: `${pctText(pct(marke, basis))} bis Marke ${betrag(markeGeld.amount)}`,
  } : null
  return {
    chanceKurz: chance?.kurz ?? '—', risikoKurz: risiko?.kurz ?? '—',
    chance: chance?.betrag ?? null, risiko: risiko?.betrag ?? null,
    chanceSub: chance?.sub ?? leer.chanceSub, risikoSub: risiko?.sub ?? leer.risikoSub,
    score,
  }
}

/** Ob irgendein Betrag der Idee eine Umrechnung in die Zielwährung trägt. */
function hatUmrechnung(e: TradeIdea, ziel: string | undefined): boolean {
  return [e.last_price, e.reference_price, e.entry_low, e.entry_high, e.chase_limit, e.invalid_below]
    .some((m) => umgerechnet(m, ziel) !== null)
}

/**
 * Aus einer Idee eine Zeile. `umrechnung` ist die gewählte Zielwährung: Gibt
 * es dafür keinen Kurs, sagt die Zeile das — sonst sähe es aus, als wäre
 * die Umrechnung vergessen worden.
 */
export function zeileAus(e: TradeIdea, umrechnung?: string): IdeenZeile {
  const risiko = e.risk_profile ? RISK_PROFILE[e.risk_profile] ?? e.risk_profile : 'ohne Risikoprofil'
  const ohneKurs = umrechnung && umrechnung !== e.currency && !hatUmrechnung(e, umrechnung)
    ? ` · ohne Kurs ${umrechnung}/${e.currency}` : ''
  const kap = e.market_cap && e.market_cap_ccy
    ? kapitalisierung(String(e.market_cap), e.market_cap_ccy) : null
  const stamm = [kap, e.sector_name ?? null].filter((s): s is string => Boolean(s))
  const ort = stamm.length ? stamm.join(' · ') : `${e.isin} · ${e.exchange} · ${e.currency}`
  return {
    key: `idee-${e.id}`, id: e.id, idee: e,
    name: e.name, ort: `${ort}${ohneKurs}`,
    headline: e.headline,
    klasse: CLASSIFICATION[e.classification] ?? e.classification,
    status: STATUS[e.status] ?? e.status, statusTon: STATUS_TON[e.status] ?? 'muted',
    meta: `${risiko} · seit ${zeitLang(e.created)}`,
    ...abstandAus(e),
    beendet: beendet(e.status),
  }
}

/** „1 Idee", „3 Ideen" */
function ideen(n: number): string { return `${n} ${n === 1 ? 'Idee' : 'Ideen'}` }

const STATUS_REIHE = ['PROPOSED', 'ACTIVE', 'ENTERED', 'INVALIDATED', 'EXPIRED', 'CLOSED', 'REJECTED']

/**
 * Die Zeilen als Gruppen.
 *
 * NACH ABSTAND: die offenen zuerst, was kaufbar ist ganz oben, die beendeten
 * als eigene Gruppe darunter. NACH STATUS: in der Reihenfolge des Lebens
 * einer Idee. NACH INSTRUMENT: alphabetisch, alle Ideen zu einem Titel
 * beieinander. NACH BRANCHE: der GICS-Sektor aus den Stammdaten, darin
 * nach Abstand; Titel ohne Einordnung stehen unter „ohne Branche".
 */
/**
 * Neueste zuerst — in JEDER Gruppe.
 *
 * Wer einmal am Tag hineinschaut, will sehen, was seit gestern dazukam;
 * das steht hinter „seit" in der Zeile. Der Abstand zum Einstieg ordnet
 * nicht mehr: Ohne Tageskurs wäre er ohnehin bei allen gleich, und mit
 * Kurs steht er groß rechts in der Zeile, wo man ihn sieht.
 */
function neuesteZuerst(a: IdeenZeile, b: IdeenZeile): number {
  return Date.parse(b.idee.created) - Date.parse(a.idee.created)
}

export function gruppiere(zeilen: IdeenZeile[], sicht: Sicht): IdeenGruppe[] {
  if (sicht === 'status') {
    return STATUS_REIHE
      .map((st) => ({
        key: st,
        label: (STATUS[st] ?? st).replace(/^./, (c) => c.toUpperCase()),
        zeilen: zeilen.filter((z) => z.idee.status === st).sort(neuesteZuerst),
      }))
      .filter((g) => g.zeilen.length > 0)
      // Kein Untertitel: Die Anzahl steht rechts, sie zweimal zu nennen
      // sagte nichts.
      .map((g) => ({ ...g, sub: '' }))
  }
  if (sicht === 'instrument') {
    const nachName = new Map<string, IdeenZeile[]>()
    for (const z of zeilen) {
      const liste = nachName.get(z.name)
      if (liste) liste.push(z)
      else nachName.set(z.name, [z])
    }
    return [...nachName.entries()]
      .sort(([a], [b]) => a.localeCompare(b, 'de'))
      .map(([name, liste]) => ({
        key: `instrument-${liste[0].idee.isin}`, label: name,
        sub: `${ideen(liste.length)} · ${liste[0].idee.sector_name ?? liste[0].idee.isin}`,
        zeilen: liste.sort(neuesteZuerst),
      }))
  }
  if (sicht === 'branche') {
    const nachBranche = new Map<string, IdeenZeile[]>()
    for (const z of zeilen) {
      const branche = z.idee.sector_name ?? 'ohne Branche'
      const liste = nachBranche.get(branche)
      if (liste) liste.push(z)
      else nachBranche.set(branche, [z])
    }
    return [...nachBranche.entries()]
      .sort(([a], [b]) => a.localeCompare(b, 'de'))
      .map(([branche, liste]) => {
        const titel = new Set(liste.map((z) => z.idee.isin)).size
        return {
          key: `branche-${branche}`, label: branche,
          sub: `${ideen(liste.length)} · ${titel} ${titel === 1 ? 'Instrument' : 'Instrumente'}`,
          zeilen: liste.sort(neuesteZuerst),
        }
      })
  }
  const offen = zeilen.filter((z) => !z.beendet).sort(neuesteZuerst)
  const zu = zeilen.filter((z) => z.beendet).sort(neuesteZuerst)
  const gruppen: IdeenGruppe[] = [
    { key: 'offen', label: 'Offen', sub: `${offen.length} · neueste zuerst`, zeilen: offen },
  ]
  if (zu.length) gruppen.push({ key: 'beendet', label: 'Beendet', sub: String(zu.length), zeilen: zu })
  return gruppen
}

/** Die Kennungen in Leserichtung — nur aus den aufgeklappten Gruppen. */
export function reiheAus(gruppen: IdeenGruppe[], zu: ReadonlySet<string>): number[] {
  return gruppen.filter((g) => !zu.has(g.key)).flatMap((g) => g.zeilen.map((z) => z.id))
}

/** Die vier Zahlen über der Tafel. */
export function kennzahlenAus(zeilen: IdeenZeile[]): {
  inZone: number; offen: number; vorgeschlagen: number; imMarkt: number
} {
  return {
    inZone: zeilen.filter((z) => z.abstand === 'in Zone').length,
    offen: zeilen.filter((z) => !z.beendet).length,
    vorgeschlagen: zeilen.filter((z) => z.idee.status === 'PROPOSED').length,
    imMarkt: zeilen.filter((z) => z.idee.status === 'ENTERED').length,
  }
}

const TAG_MONAT = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit' })

/** „Kurse vom 04.09. bis 07.09." — oder nur ein Tag, oder nichts. */
export function kursZeitraum(zeilen: IdeenZeile[]): string {
  const tage = zeilen.map((z) => z.idee.last_price_at).filter((t): t is string => Boolean(t))
    .map((t) => new Date(t).getTime())
  if (tage.length === 0) return ''
  const von = TAG_MONAT.format(new Date(Math.min(...tage)))
  const bis = TAG_MONAT.format(new Date(Math.max(...tage)))
  return von === bis ? `Kurse vom ${von}` : `Kurse vom ${von} bis ${bis}`
}

/** Links im Fuß der Tafel. */
export function fussAus(zeilen: IdeenZeile[], waehrung: string): string {
  const teile = [ideen(zeilen.length), kursZeitraum(zeilen),
                 waehrung
                   ? `Umrechnung ≈ in ${waehrung} an den Marken, wo ein Kurs vorliegt`
                   : 'in Originalwährung']
  return teile.filter(Boolean).join(' · ')
}

// ------------------------------------------------------------ Kursleiter

export type LeiterTon = 'accent' | 'bad' | 'good' | 'warn' | 'muted'

export interface LeiterSegment {
  key: string
  /** Prozent der Breite. */
  links: number
  breite: number
  art: 'tot' | 'zone' | 'teuer'
}

export interface LeiterMarke {
  key: string
  x: number
  /** „20,14" — oder mit Zielwährung „≈ 17,27 EUR". */
  wert: string
  /** Der gehandelte Betrag unter dem umgerechneten: „20,14 USD". */
  original?: string
  label: string
  ton: LeiterTon
  fett: boolean
  /** Wo die Fahne steht — `null` heißt: nur der Strich. */
  seite: 'oben' | 'unten' | null
  zeile: number
}

export interface Leiter {
  segmente: LeiterSegment[]
  marken: LeiterMarke[]
  zeilenOben: number
  zeilenUnten: number
  /** Eine beendete Idee — die Leiter steht gedämpft. */
  tot: boolean
}

/** Wie nah zwei Fahnen einer Zeile einander kommen dürfen, in Prozent. */
const MIN_ABSTAND = { gross: 10, klein: 12 }

/**
 * Die Schwellen einer Idee auf einer Achse.
 *
 * Die Skala spannt sich über die vorhandenen Werte mit sieben Prozent Luft.
 * Klein (in der Zeile) tragen nur die wichtigen Marken eine Fahne — Kurs,
 * widerlegt, Zone, nicht nachlaufen — in einer Zeile über und einer unter
 * der Achse; groß (aufgeklappt) kommen Referenz, Schnitt und Ziele dazu, in
 * zwei Zeilen je Seite. Was keinen Platz findet, behält seinen Strich.
 *
 * Ohne Zone gibt es keine Leiter: Dann wird nur beobachtet.
 *
 * DIE ACHSE BLEIBT IN DER HANDELSWÄHRUNG, die Fahnen nicht: Mit Zielwährung
 * steht an jeder Marke der umgerechnete Betrag oben und der gehandelte
 * darunter, beide mit Währung — sobald zwei Zahlen übereinanderstehen, muss
 * an jeder stehen, worin sie notiert ist.
 */
export function leiterAus(e: TradeIdea, ziele: Money[] = [], gross = false,
                          umrechnung?: string): Leiter | null {
  const kurs = zahl(e.last_price)
  const widerlegt = zahl(e.invalid_below)
  const zoneAb = zahl(e.entry_low)
  const zoneBis = zahl(e.entry_high)
  const chase = zahl(e.chase_limit)
  const referenz = zahl(e.reference_price)
  const schnitt = zahl(e.planned_average)
  const zielWerte = ziele.map(zahl).filter((v): v is number => v !== null)
  if (zoneAb === null) return null

  const werte = [widerlegt, zoneAb, zoneBis, chase, referenz, schnitt, kurs, ...zielWerte]
    .filter((v): v is number => v !== null)
  const lo = Math.min(...werte)
  const hi = Math.max(...werte)
  const luft = (hi - lo) * 0.07 || 1
  const min = lo - luft
  const max = hi + luft
  const x = (v: number) => ((v - min) / (max - min)) * 100

  const tot = beendet(e.status)
  const zb = zoneBis ?? zoneAb

  const segmente: LeiterSegment[] = []
  if (widerlegt !== null) segmente.push({ key: 'tot', links: 0, breite: x(widerlegt), art: 'tot' })
  if (zoneBis !== null) segmente.push({ key: 'zone', links: x(zoneAb), breite: x(zb) - x(zoneAb), art: 'zone' })
  if (chase !== null) segmente.push({ key: 'teuer', links: x(zb), breite: Math.max(0, x(chase) - x(zb)), art: 'teuer' })

  const zeilenOben = gross ? 2 : 1
  const zeilenUnten = gross ? 2 : 1
  const belegt = { oben: [[], []] as number[][], unten: [[], []] as number[][] }
  const abstand = gross ? MIN_ABSTAND.gross : MIN_ABSTAND.klein
  const marken: LeiterMarke[] = []

  const marke = (key: string, geld: Money | null, ton: LeiterTon, label: string,
                 seite: 'oben' | 'unten', wichtig: boolean, fett = false) => {
    const v = zahl(geld)
    if (v === null) return
    const X = x(v)
    const um = umgerechnet(geld, umrechnung)
    const basis = um
      ? { key, x: X, wert: um, original: `${betrag(geld!.amount)} ${geld!.currency}`, label, ton, fett }
      : { key, x: X, wert: betrag(String(v)), label, ton, fett }
    if (!gross && !wichtig) { marken.push({ ...basis, seite: null, zeile: 0 }); return }
    const frei = (s: 'oben' | 'unten') =>
      belegt[s].findIndex((zeile) => zeile.every((p) => Math.abs(p - X) >= abstand))
    let s = seite
    let zeile = frei(s)
    if (zeile < 0) { s = s === 'oben' ? 'unten' : 'oben'; zeile = frei(s) }
    const platz = s === 'oben' ? zeilenOben : zeilenUnten
    if (zeile < 0 || zeile >= platz) { marken.push({ ...basis, seite: null, zeile: 0 }); return }
    belegt[s][zeile].push(X)
    marken.push({ ...basis, seite: s, zeile })
  }

  marke('kurs', e.last_price, tot ? 'bad' : 'accent', 'Kurs', 'oben', true, true)
  marke('widerlegt', e.invalid_below, 'bad', 'widerlegt', 'unten', true)
  marke('zoneAb', e.entry_low, 'good', e.classification === 'TRIGGER_ONLY' ? 'Auslöser' : 'Zone ab', 'unten', true)
  if (zoneBis !== null) marke('zoneBis', e.entry_high, 'good', 'bis', 'unten', true)
  marke('chase', e.chase_limit, 'warn', 'nicht nachlaufen', 'oben', true)
  // Ohne Kurs traegt die Referenz die Fahne: Sonst stuende in der Zeile
  // keine Zahl, von der aus die Zone zu lesen waere.
  if (referenz !== null && referenz !== kurs) {
    marke('referenz', e.reference_price, 'muted', 'Referenz', 'oben', kurs === null)
  }
  marke('schnitt', e.planned_average, 'muted', 'Schnitt', 'oben', false)
  ziele.forEach((z, i) => marke(`ziel${i}`, z, 'good', `Ziel ${i + 1}`, i % 2 ? 'oben' : 'unten', false))

  return { segmente, marken, zeilenOben, zeilenUnten, tot }
}
